import { checkDateValidity } from '../../../../Foundation/utils';
import { Dropable } from '../../../dropdown';
import {
  ACTION_DATE_FIELDS,
  isEarmouldDisabledForFlow,
  isLossAndDamageUsed,
} from '../utils/flow-expiry';

/**
 * Safely retrieves dynamically-rendered rows from tbody.
 * Returns an empty array when tbody is null or rows have not been rendered yet.
 *
 * Rows are generated dynamically in index.js (_createRow, via createElement),
 * so they are NOT available through component refs (e.g. this._refs.rows).
 * Always query the live DOM through this helper instead.
 */
function getRenderedRows(tbody) {
  if (!tbody) return [];
  return Array.from(tbody.querySelectorAll('.service-results-table__row'));
}

/**
 * Renders dropdown action items inside every non-returned row and
 * initialises the Dropable component on each ui-dropdown wrapper.
 * Rows that have no ui-dropdown container (e.g. returned rows) are skipped.
 * Safe to call before rows exist – it simply does nothing.
 *
 * @param {HTMLElement|null} tbody   - the <tbody> containing dynamically generated rows
 * @param {Array}            actions - e.g. [{ label: 'Repair', url: '#repair', actionType: 'repair' }]
 * @param {string}           cancelLabel - translated label for the cancel action
 * @param {Function}         [onActionClick] - called with (event, serialNo, link); bound here because portaled menus leave the row
 */
export function renderDropdownItems(tbody, actions, cancelLabel, onActionClick) {
  const rows = getRenderedRows(tbody);
  if (!rows.length || !Array.isArray(actions) || !actions.length) return;

  rows.forEach((row) => {
    const wrapper = row.querySelector('.ui-dropdown');
    if (!wrapper) return;

    const menu = wrapper.querySelector('.ui-dropdown-menu');
    if (!menu) return;

    menu.innerHTML = '';

    actions.forEach((action) => {
      const li = document.createElement('li');
      li.className = 'ui-dropdown-menu__item';

      if (action.actionType) {
        li.setAttribute('data-action-type', action.actionType);
      }

      const a = document.createElement('a');
      const actionUrl = new URL(action.url, window.location.href);
      actionUrl.searchParams.set('id', row.dataset.serialNo || '');

      a.href = actionUrl.toString();
      if (typeof onActionClick === 'function') {
        a.addEventListener('click', (event) => onActionClick(event, row.dataset.serialNo, a));
      }
      const icon = document.createElement('span');
      icon.className = 'ui-dropdown-menu__item--icon';

      a.appendChild(document.createTextNode(action.label));
      a.appendChild(icon);

      li.appendChild(a);
      menu.appendChild(li);
    });

    const cancelAction = document.createElement('li');
    cancelAction.className = 'ui-dropdown-menu__item ui-dropdown-menu__item--cancel';

    const cancelButton = document.createElement('button');
    cancelButton.textContent = cancelLabel;
    cancelButton.addEventListener('click', () => wrapper.Dropable.close());
    cancelAction.appendChild(cancelButton);
    menu.appendChild(cancelAction);

    // Initialise or re-initialise the Dropable component so that
    // click/keyboard handlers and portal logic are wired up.
    if (!wrapper.Dropable) {
      new Dropable(wrapper);
    }
  });
}

/**
 * Shows / hides individual dropdown items per row based on warranty data.
 * Uses the existing ui-dropdown selectors (ui-dropdown__trigger,
 * ui-dropdown-menu, data-action-type) so it stays in sync with
 * dropdown/index.js and _dropdown.scss.
 *
 * Safe to call before rows exist – it simply does nothing.
 *
 * @param {HTMLElement|null} tbody      - the <tbody> with dynamically generated rows
 * @param {Array}            dataArray  - the warranty data array matching the rendered rows
 * @param {object}           config     - table config
 */
export function updateActionVisibility(tbody, dataArray, config) {
  const rows = getRenderedRows(tbody);
  if (!rows.length || !Array.isArray(dataArray)) return;

  rows.forEach((row, index) => {
    const rowData = dataArray[index];
    if (!rowData) return;

    const warrantyDates = [
      rowData.RepairWarrantyEndDate,
      rowData.RemakeWarrantyEndDate,
      rowData.ReturnForCreditEndDate,
    ];

    if (!rowData.IsLossAndDamageUsed) {
      warrantyDates.push(rowData.LostAndDamageWarrantyEndDate);
    }

    const hasNoWarrantyDates = warrantyDates.every((d) => !checkDateValidity(d));

    if (hasNoWarrantyDates) {
      row.classList.add('service-results-table__row--no-actions');
      return;
    }

    const triggerBtn = row.querySelector('.ui-dropdown__trigger');
    const dropdownWrapper = triggerBtn && triggerBtn.closest('.ui-dropdown');
    const rowMenu =
      dropdownWrapper && dropdownWrapper.Dropable && dropdownWrapper.Dropable._refs.menu;
    if (!rowMenu) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let visibleActions = 0;

    Object.entries(ACTION_DATE_FIELDS).forEach(([actionType, { field, hideExpired }]) => {
      const item = rowMenu.querySelector(`[data-action-type="${actionType}"]`);
      if (!item) return;

      const date = rowData[field] ? new Date(rowData[field]) : null;
      const isVisible =
        checkDateValidity(date) &&
        (!hideExpired || date >= today) &&
        !isEarmouldDisabledForFlow(actionType, rowData, config) &&
        !isLossAndDamageUsed(field, rowData);

      item.classList.toggle('is-hidden', !isVisible);
      if (isVisible) visibleActions += 1;
    });

    row.classList.toggle('service-results-table__row--no-actions', visibleActions === 0);
  });
}
